import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '../components/Navigation/routes';
import SEO from '../components/SEO';
import {
  BookOpen,
  Building2,
  FileText,
  Newspaper,
  ArrowUpRight,
  Share2,
  Twitter,
  Linkedin,
  Facebook,
  Link2,
  Check,
  Wrench,
  UserCircle,
  Zap,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { fetchResources } from '../services/resourcesService';

// ── Constants ────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'all',                label: 'All',                Icon: BookOpen,  path: ROUTES.RESOURCES                   },
  { id: 'industry-solution', label: 'Industry Solutions', Icon: Building2, path: ROUTES.RESOURCES_INDUSTRY_SOLUTIONS },
  { id: 'case-study',        label: 'Case Studies',       Icon: FileText,  path: ROUTES.RESOURCES_CASE_STUDIES       },
  { id: 'article',           label: 'Articles',           Icon: Newspaper, path: ROUTES.RESOURCES_ARTICLES           },
];

const PATH_TO_CATEGORY = {
  [ROUTES.RESOURCES_ARTICLES]:           'article',
  [ROUTES.RESOURCES_CASE_STUDIES]:       'case-study',
  [ROUTES.RESOURCES_INDUSTRY_SOLUTIONS]: 'industry-solution',
};

const CATEGORY_BADGE = {
  'industry-solution': {
    label: 'Industry Solution',
    cls: 'bg-purple-100 text-purple-700 border-purple-200',
  },
  'case-study': {
    label: 'Case Study',
    cls: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  'article': {
    label: 'Article',
    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
};

// ── Share popover ────────────────────────────────────────────

const SharePopover = ({ title, pageUrl }) => {
  const [open, setOpen]     = useState(false);
  const [copied, setCopied] = useState(false);
  const ref                 = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const encoded      = encodeURIComponent(pageUrl);
  const encodedTitle = encodeURIComponent(title);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium
          border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800
          bg-white transition-colors"
      >
        <Share2 className="w-3.5 h-3.5" />
        Share
        <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-20 w-48 rounded-xl border border-gray-200 bg-white shadow-xl py-1.5 origin-top-right">
          <a
            href={`https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Twitter className="w-4 h-4 text-sky-500" />
            Share on X
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Linkedin className="w-4 h-4 text-blue-600" />
            Share on LinkedIn
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Facebook className="w-4 h-4 text-blue-700" />
            Share on Facebook
          </a>
          <div className="border-t border-gray-100 my-1" />
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-emerald-500" />
                <span className="text-emerald-600 font-medium">Copied!</span>
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4 text-gray-500" />
                Copy link
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// ── Resource card ─────────────────────────────────────────────

const ResourceCard = ({ resource }) => {
  const origin     = typeof window !== 'undefined' ? window.location.origin : '';
  const isExternal = !!resource.external_url && !resource.page_path;
  const target     = resource.page_path || resource.external_url || '#';
  const pageUrl    = resource.page_path
    ? `${origin}${resource.page_path}`
    : resource.external_url ?? origin;

  const badge = CATEGORY_BADGE[resource.category] ?? {
    label: resource.category,
    cls: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  const hasTools       = resource.tool_tags?.length > 0;
  const hasDisciplines = resource.discipline_tags?.length > 0;
  const hasRoles       = resource.role_tags?.length > 0;
  const hasFeatures    = resource.feature_tags?.length > 0;
  const hasTags        = hasTools || hasDisciplines || hasRoles || hasFeatures;

  const ReadButton = () =>
    isExternal ? (
      <a
        href={target}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium
          bg-gradient-to-r from-purple-600 to-pink-600 text-white
          hover:from-purple-700 hover:to-pink-700 shadow-sm shadow-purple-200 transition-colors"
      >
        Read <ArrowUpRight className="w-3.5 h-3.5" />
      </a>
    ) : (
      <Link
        to={target}
        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium
          bg-gradient-to-r from-purple-600 to-pink-600 text-white
          hover:from-purple-700 hover:to-pink-700 shadow-sm shadow-purple-200 transition-colors"
      >
        Read <ArrowUpRight className="w-3.5 h-3.5" />
      </Link>
    );

  return (
    <article className="group flex flex-col sm:flex-row rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-200 overflow-hidden">

      {/* Cover image */}
      {resource.cover_image_url && (
        <div className="sm:w-52 sm:shrink-0 h-44 sm:h-auto overflow-hidden bg-gray-100">
          <img
            src={resource.cover_image_url}
            alt={resource.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
          />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col flex-1 p-5 gap-3 min-w-0">

        {/* Category badge + title */}
        <div className="flex flex-wrap items-center gap-2">
          <span className={`shrink-0 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${badge.cls}`}>
            {badge.label}
          </span>
          <h3 className="text-base font-bold text-gray-900 leading-snug">
            {resource.title}
          </h3>
        </div>

        {/* Excerpt */}
        {resource.excerpt && (
          <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
            {resource.excerpt}
          </p>
        )}

        {/* Tags */}
        {hasTags && (
          <div className="flex flex-wrap gap-1.5">
            {resource.tool_tags?.map((slug) => (
              <Link
                key={slug}
                to={`/library/aec/${slug}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border
                  border-violet-200 bg-violet-50 text-violet-700
                  hover:bg-violet-100 hover:border-violet-300 transition-colors"
              >
                <Wrench className="w-2.5 h-2.5 shrink-0" />
                {slug}
              </Link>
            ))}
            {resource.discipline_tags?.map((d) => (
              <span
                key={d}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
                  border border-blue-200 bg-blue-50 text-blue-700"
              >
                <Building2 className="w-2.5 h-2.5 shrink-0" />
                {d}
              </span>
            ))}
            {resource.role_tags?.map((r) => (
              <span
                key={r}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
                  border border-amber-200 bg-amber-50 text-amber-700"
              >
                <UserCircle className="w-2.5 h-2.5 shrink-0" />
                {r}
              </span>
            ))}
            {resource.feature_tags?.map((f) => (
              <span
                key={f}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
                  border border-gray-200 bg-gray-50 text-gray-600"
              >
                <Zap className="w-2.5 h-2.5 shrink-0" />
                {f}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto pt-1 flex items-center gap-2 flex-wrap">
          <ReadButton />
          <SharePopover title={resource.title} pageUrl={pageUrl} />
        </div>
      </div>
    </article>
  );
};

// ── Skeleton ──────────────────────────────────────────────────

const ResourceCardSkeleton = () => (
  <div className="flex flex-col sm:flex-row rounded-2xl border border-gray-100 bg-white overflow-hidden animate-pulse">
    <div className="sm:w-52 h-44 sm:h-full bg-gray-100 shrink-0" />
    <div className="flex flex-col flex-1 p-5 gap-3">
      <div className="flex gap-2 items-center">
        <div className="h-5 w-28 rounded-full bg-gray-100" />
        <div className="h-5 w-48 rounded bg-gray-100" />
      </div>
      <div className="h-4 w-full rounded bg-gray-100" />
      <div className="h-4 w-3/4 rounded bg-gray-100" />
      <div className="flex gap-1.5 flex-wrap">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-5 w-20 rounded-full bg-gray-100" />
        ))}
      </div>
      <div className="flex gap-2 mt-auto pt-1">
        <div className="h-8 w-20 rounded-lg bg-gray-100" />
        <div className="h-8 w-24 rounded-lg bg-gray-100" />
      </div>
    </div>
  </div>
);

// ── Page ──────────────────────────────────────────────────────

const Resources = () => {
  const location = useLocation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // Derive active category entirely from the URL — no state needed
  const activeCategory = PATH_TO_CATEGORY[location.pathname] ?? 'all';

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchResources({ category: activeCategory === 'all' ? null : activeCategory })
      .then(setResources)
      .catch((err) => setError(err.message ?? 'Failed to load resources'))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const counts = resources.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Resources & Insights — Industry Solutions, Case Studies & Articles | STRAY Apps"
        description="Industry solutions, case studies, and articles from our work across AEC, construction tech, and digital innovation."
        path={location.pathname}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-white border-b border-gray-100 py-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/3 w-80 h-80 bg-purple-500/8 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-pink-500/8 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-xs font-semibold text-purple-500 uppercase tracking-widest mb-4">
            <BookOpen className="w-4 h-4" />
            Resources
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3 leading-tight">
            Resources & Insights
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl leading-relaxed">
            Industry solutions, case studies, and articles from our work across AEC,
            construction tech, and digital innovation.
          </p>
        </div>
      </section>

      {/* ── Category tabs ── */}
      <div className="sticky top-[69px] z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2.5 scrollbar-hide">
            {CATEGORIES.map(({ id, label, Icon, path }) => (
              <Link
                key={id}
                to={path}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all no-underline ${
                  activeCategory === id
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm shadow-purple-200'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Result count */}
        {!loading && !error && counts > 0 && (
          <p className="text-sm text-gray-400 mb-5">
            {counts} {counts === 1 ? 'resource' : 'resources'}
          </p>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col gap-4">
            {[1, 2, 3].map((i) => <ResourceCardSkeleton key={i} />)}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex items-center justify-center py-20 text-red-500 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {error}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && resources.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <Newspaper className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-700 font-semibold mb-1">No resources yet</p>
            <p className="text-sm text-gray-400">
              {activeCategory === 'article'
                ? 'Articles are coming soon — check back shortly.'
                : 'Nothing in this category yet.'}
            </p>
          </div>
        )}

        {/* Cards */}
        {!loading && !error && resources.length > 0 && (
          <div className="flex flex-col gap-4">
            {resources.map((r) => (
              <ResourceCard key={r.id} resource={r} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Resources;
